import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect } from "react";
import { loginRequest } from "../config/msalConfig";
import { storeOAuthReturnPath } from "../utils/oauthReturnHandler";
import { activePhoneSession, hasPhoneSession } from "../utils/phoneSession";
import { authService } from "../utils/authService";

/**
 * ProtectedRoute Component
 *
 * Ensures that children components are only rendered when the user is authenticated.
 * Automatically redirects to login if no authenticated account is found.
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <YourProtectedComponent />
 * </ProtectedRoute>
 * ```
 *
 * @param children - React components to render when authenticated
 */
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  // Signed in with a mobile number: there is no MSAL account, and there needn't be.
  const phoneSignedIn = activePhoneSession() !== null;

  useEffect(() => {
    // A phone sign-in that has run out goes back to the sign-in page, never to
    // Microsoft's, which a phone-only user can't complete.
    if (!phoneSignedIn && hasPhoneSession()) {
      authService.phoneSignInEnded();
      return;
    }
    // Trigger login redirect if no authentication is in progress and no accounts exist
    if (!phoneSignedIn && inProgress === InteractionStatus.None && accounts.length === 0) {
      const currentPath = window.location.pathname + window.location.search;
      if (currentPath !== "/" && !currentPath.startsWith("/callback")) {
        storeOAuthReturnPath(currentPath);
      }
      instance.loginRedirect(loginRequest);
    }
  }, [phoneSignedIn, inProgress, accounts, instance]);

  if (phoneSignedIn) return <>{children}</>;

  // Wait while authentication interaction is in progress (startup overlay covers this)
  if (inProgress !== InteractionStatus.None) {
    return null;
  }

  // Only render children if user is authenticated (has accounts)
  return accounts.length > 0 ? <>{children}</> : null;
};
